import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LoginShell } from "../components/login/LoginShell";
import { PasswordSignIn } from "../components/login/PasswordSignIn";
import { ForgotPassword } from "../components/login/ForgotPassword";
import { MfaVerify } from "../components/login/MfaVerify";
import { RecoveryCodes } from "../components/login/RecoveryCodes";
import { ChangePassword } from "../components/login/NewPassword";
import { TwoFactorSetup } from "../components/TwoFactorSetup";

// /login: the Coming-Soon screen while the site is gated, the password sign-in while it is open.
// Steps: sign in -> (2-step code | first-time 2-step setup -> recovery codes) -> (new password) -> back where you were.
export default function LoginPage() {
  const { gated, user, sessionReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("signin");
  const [email, setEmail] = useState("");
  const [codes, setCodes] = useState(null);
  const from = location.state?.from && location.state.from !== "/login" ? location.state.from : "/";
  const done = () => navigate(from, { replace: true });

  if (!sessionReady) {
    return <div data-testid="login-loading" className="grid min-h-screen place-items-center bg-[#efe4cc]"><Loader2 className="h-6 w-6 animate-spin text-[#6b6b73]" /></div>;
  }
  const current = user?.must_change_password && !["recovery"].includes(step) ? "change-password" : step;
  if (user && current === "signin") return <Navigate to={from} replace />;

  const afterPassword = (data) => {
    if (data.mfa_required) return setStep("mfa");
    if (data.mfa_setup_required) return setStep("mfa-setup");
    return data.must_change_password ? setStep("change-password") : done();
  };
  const afterMfa = (data) => (data.must_change_password ? setStep("change-password") : done());
  const afterSetup = (data) => {
    setCodes({ list: data.recovery_codes, then: data.must_change_password ? "change-password" : null });
    setStep("recovery");
  };

  const views = {
    signin: <PasswordSignIn gated={gated} email={email} setEmail={setEmail} onNext={afterPassword} onForgot={() => setStep("forgot")} />,
    forgot: <ForgotPassword email={email} setEmail={setEmail} onBack={() => setStep("signin")} />,
    mfa: <MfaVerify onDone={afterMfa} onRestart={() => setStep("signin")} />,
    "mfa-setup": <TwoFactorSetup onEnabled={afterSetup} />,
    recovery: codes && <RecoveryCodes codes={codes.list} onContinue={() => (codes.then ? setStep(codes.then) : done())} />,
    "change-password": <ChangePassword onDone={done} />,
  };
  return <LoginShell gated={gated}>{views[current] || views.signin}</LoginShell>;
}
